/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */

import _forEach from 'lodash/forEach';
import ConfigDeviceSettings from '../../config/device.config';
import AppConst from '../../constants/app.constant';
import ChartConst from '../../constants/chart.constant';
import DefConst from '../../constants/def.constant';
import EventConst from '../../constants/event.constant';
import utilityService from '../../services/utility.service';
import bnChartDataService from '../../services/chartData.service';
import bnChartDataProcessService from '../../services/chartDataProcess.service';
import routeProfileDataSource from './Routeprofile.data';
import gridViewDataSource from '../Gridview/Gridview.data';
import mapViewDataSource from '../Leafletmapview/Leafletmapview.data';
import globeViewDataSource from '../Mapview/Mapview.data';
import Emitter from '../../bus';
import d3 from '../../globalexports/d3.export';
import router from '../../router';

function routeProfileService() {
  const travelRouteMapKey = d3.timeFormat(ChartConst.mapKeyCreator);
  const aloftRouteMapKey = d3.timeFormat(ChartConst.mapKeyCreator);
  let travelRouteMap = {};
  let travelRouteMapNaksha = {};
  let travelRouteGridNaksha = {};
  let travelRouteGlobeNaksha = {};
  let aloftRouteMap = {};
  const content = {};

  function getTravelRouteMapObj(settings) {
    switch (settings.viewName) {
      case AppConst.stateMap.routeprofile.key:
        return travelRouteMap;
      case AppConst.stateMap.leafletmapview.key:
        return travelRouteMapNaksha;
      case AppConst.stateMap.mapview.key:
        return travelRouteGlobeNaksha;
      case AppConst.stateMap.gridview.key:
        return travelRouteGridNaksha;
      default:
        return null;
    }
  }

  function resetTravelRouteMapObj(settings) {
    switch (settings.viewName) {
      case AppConst.stateMap.routeprofile.key:
        travelRouteMap = {};
        aloftRouteMap = {};
        break;
      case AppConst.stateMap.leafletmapview.key:
        travelRouteMapNaksha = {};
        break;
      case AppConst.stateMap.mapview.key:
        travelRouteGlobeNaksha = {};
        break;
      case AppConst.stateMap.gridview.key:
        travelRouteGridNaksha = {};
        break;
      default:
    }
  }

  function getDataSourceFactory(settings) {
    switch (settings.viewName) {
      case AppConst.stateMap.routeprofile.key:
        return routeProfileDataSource;
      case AppConst.stateMap.leafletmapview.key:
        return mapViewDataSource;
      case AppConst.stateMap.mapview.key:
        return globeViewDataSource;
      case AppConst.stateMap.gridview.key:
        return gridViewDataSource;
      default:
        return null;
    }
  }
  // console.log(getDataSourceFactory());

  // flightMode can be MAV, LAMP or NA
  function getTravelRoute(data, settings, flightMode) {
    const versionToUse = ConfigDeviceSettings.settings.apiVersionKey;
    let i = 0;
    const travelRoute = [];
    let closestRecord;
    let closestRecordSlider;
    let arrivalTime;
    let arrivalTimeSlider;
    _forEach(data, (item) => {
      if (settings.viewName === AppConst.stateMap.leafletmapview.key
        || settings.viewName === AppConst.stateMap.mapview.key) {
        arrivalTime = utilityService.addHours(new Date(settings.departureTime), 0);
        arrivalTimeSlider = utilityService.addHours(
          new Date(settings.departureTime),
          item.travel_time,
        );
      } else {
        arrivalTime = utilityService.addHours(new Date(settings.departureTime), item
          .travel_time);
      }
      travelRoute.push({
        icao_id: item.icao_id,
        travel_time: item.travel_time,
        flightDirection: item.flightDirection,
        airportDetails: utilityService.getMatchedAirportObject(item.icao_id),
        arrival_time: arrivalTime,
        arrivalTimeSlider,
        faa_facility_name: item.faa_facility_name,
      });
    });
    while (i < travelRoute.length) {
      closestRecord = bnChartDataService.findClosestRecord(data[i], travelRoute[i].arrival_time);
      if (settings.viewName === AppConst.stateMap.leafletmapview.key
        || settings.viewName === AppConst.stateMap.mapview.key) {
        closestRecordSlider = bnChartDataService.findClosestRecord(
          data[i],
          travelRoute[i].arrivalTimeSlider,
        );
      }
      travelRoute[i].closest_forecast_ts = closestRecord?.forecast_ts;
      // Do the union of properties inside two object
      travelRoute[i] = Object.assign(travelRoute[i], closestRecord);
      if (settings.viewName === AppConst.stateMap.leafletmapview.key
        || settings.viewName === AppConst.stateMap.mapview.key) {
        travelRoute[i].frlSlider = closestRecordSlider.frl;
      } else {
        travelRoute[i].frlSlider = closestRecord.frl;
      }
      // converting wsp from mph to knots
      // TBD : share it with bnChart service
      travelRoute[i].wsp = (travelRoute[i].wsp !== null && travelRoute[i].wsp !== 'undefined')
        ? parseFloat(travelRoute[i].wsp) / 1.15078
        : null;
      travelRoute[i].xwd = (travelRoute[i].xwd !== null && travelRoute[i].xwd !== 'undefined')
        ? parseFloat(travelRoute[i].xwd) / 1.15078
        : null;
      travelRoute[i].wgs = (travelRoute[i].wgs !== null && travelRoute[i].wgs !== 'undefined')
        ? parseFloat(travelRoute[i].wgs) / 1.15078
        : null;
      // Mapping values using DefConstant
      travelRoute[i].flightMode = flightMode;
      if (versionToUse !== 3 && travelRoute[i].cld
        && (travelRoute[i].cld !== DefConst.clouds.broken
          && travelRoute[i].cld !== DefConst.clouds.overcast)) {
        travelRoute[i].cig = 8;
      }
      travelRoute[i].q06_mapped = ChartConst.mavDefsMap.q06[travelRoute[i].q06];
      travelRoute[i].tmp_fahrenheit = travelRoute[i].tmp;
      travelRoute[i].tmp_celsius = utilityService.fahrenheitToCelsius(travelRoute[i].tmp);
      travelRoute[i].sunMoonPositionData = utilityService.getSunMoonCalculation(
        travelRoute[i].arrival_time,
        travelRoute[i].lat,
        travelRoute[i].lng,
      );
      travelRoute[i] = bnChartDataProcessService.appendWeatherStripData(
        travelRoute[i],
        'arrival_time',
      );
      i += 1;
    }
    _forEach(travelRoute.slice().reverse(), (item, index, object) => {
      if (item.skipAirport) {
        travelRoute.splice(object.length - 1 - index, 1);
      }
    });
    if (getTravelRouteMapObj(settings)) {
      getTravelRouteMapObj(settings)[travelRouteMapKey(settings.departureTime)] = travelRoute;
    }
    return travelRoute;
  }
  content.resetTravelAloftMap = () => {
    aloftRouteMap = {};
  };
  content.sliderDotsMergeWidthCalculate = () => {
    d3.selectAll('.flight-rule-slider .rz-tick').filter((...args) => {
      const index = args[1];
      const selfEle = utilityService.getSelfElement(args);
      if (index === 1) {
        const transformData = utilityService.getTransformationNonSvg(d3.select(selfEle).style(
          'transform',
        ));
        // Increase width of all except the last tick div box
        d3.selectAll('ul.rz-ticks>li:not(:last-child)').style(
          'width',
          `${transformData + 1}px`,
        );
      }
      return index === 1;
    });
  };

  content.getFlightMode = (
    departureTime,
    totalFlightDuration,
  ) => bnChartDataService.isMavOrLampFlight(
    departureTime,
    totalFlightDuration,
  );

  function findClosestAloftRecord(gribRecords, airportItem) {
    let i = 0;
    const stampsCollection = [];
    let closestRecord;
    if (gribRecords) {
      _forEach(gribRecords, (item) => {
        stampsCollection.push(item.model_ts);
      });
    }
    const closestDateObj = bnChartDataService.findClosestDate(
      airportItem.arrival_time,
      stampsCollection,
    );
    while (i < gribRecords.length) {
      if (gribRecords[i].model_ts === closestDateObj.forecast_ts) {
        closestRecord = gribRecords[i];
        closestRecord.arrival_time = airportItem.arrival_time;
        closestRecord.icao_id = airportItem.icao_id;
        break;
      }
      i += 1;
    }
    return closestRecord;
  }

  function getAloftFromMapObject(departureTime) {
    return aloftRouteMap[utilityService.getOrientation() + aloftRouteMapKey(departureTime)];
  }

  function useAloftFromMapObject(departureTime) {
    const mappedObj = getAloftFromMapObject(departureTime);
    return mappedObj;
  }
  function getEmptyAloftAltitude() {
    return {
      hgt: null,
      icsev: null,
      rh: null,
      tmp: null,
      ugrd: null,
      vgrd: null,
      vvel: null,
    };
  }
  function getTravelRouteAloftData(allAloftData, allAirportsVisible, departureTime) {
    const aloftCloudsForTravelRoute = [];
    if (allAirportsVisible) {
      _forEach(allAirportsVisible, (airport, key) => {
        if (allAloftData[key]) {
          const closestRecordOfAirport = findClosestAloftRecord(
            allAloftData[key].airports[0].grib,
            airport,
          );
          closestRecordOfAirport.flightDirection = airport.flightDirection;
          aloftCloudsForTravelRoute.push(findClosestAloftRecord(
            allAloftData[key].airports[0].grib,
            airport,
          ));
        }
      });
    }
    if (aloftCloudsForTravelRoute.length) {
      allAirportsVisible.forEach((item, idx) => {
        if (!aloftCloudsForTravelRoute[idx] || item.icao_id !== aloftCloudsForTravelRoute[idx]
          .icao_id) {
          const altitudes = [];
          for (let i = 0; i < aloftCloudsForTravelRoute[0].altitudes.length; i += 1) {
            altitudes.push(getEmptyAloftAltitude());
          }
          aloftCloudsForTravelRoute.splice(idx, 0, {
            arrival_time: item.arrival_time,
            icao_id: item.icao_id,
            altitudes,
          });
        }
      });
      aloftRouteMap[
        utilityService.getOrientation() + aloftRouteMapKey(departureTime)
      ] = utilityService.clone(
        aloftCloudsForTravelRoute,
      );
    }
    // broadcast draw aloft only when there is some record in aloftCloudsForTravelRoute
    Emitter.emit(EventConst.routeAloftReady, aloftCloudsForTravelRoute);
  }

  function allWithProgress(promises, allAirportsVisible, settings, progress, departureTime) {
    const total = promises.length;
    let now = 0;
    _forEach(promises, (p) => {
      p.then(() => {
        now += 1;
        progress(now / total, now);
      });
    });
    return Promise.all(promises).then((allAloftData) => {
      getTravelRouteAloftData(allAloftData, allAirportsVisible, departureTime, settings);
    });
  }

  function getAloftFromCache() {
    // var aloftCache = CacheFactory.get(AppConst.apiCaches.gribCache);
    // var aloftCacheData;
    // if (aloftCache) {
    //     aloftCacheData = aloftCache.get(httpService.getAloftProjection(lat, lng));
    // }
    // if (aloftCacheData && aloftCacheData[1]) {
    //     return true;
    // } else {
    //     return false;
    // }
    return false;
  }

  function checkAloftCache(allAirportsVisible, attachPromise, maximumAltitudeAllowed) {
    return new Promise((resolve) => {
      const promises = [];
      let anyUndefined = false;
      let cacheObj;
      let retObj;
      let i = 0;
      const howManyTimes = allAirportsVisible.length;

      function f() {
        if (allAirportsVisible && allAirportsVisible[i]) {
          cacheObj = getAloftFromCache(
            allAirportsVisible[i].lat,
            allAirportsVisible[i].lng,
            maximumAltitudeAllowed,
          );
        }
        if (cacheObj === false) {
          anyUndefined = true;
        }
        if (attachPromise) {
          if (allAirportsVisible[i]) {
            promises.push(bnChartDataService.getAloftData(
              allAirportsVisible[i].lat,
              allAirportsVisible[i].lng,
              allAirportsVisible[i].arrival_time,
              AppConst.dataMode.routeprofile,
              allAirportsVisible[i].icao_id,
              i,
              maximumAltitudeAllowed,
            ));
          }
        }
        i += 1;
        if (i < howManyTimes) {
          setTimeout(f, AppConst.gribRequestLag);
        } else {
          retObj = {
            anyUndefined,
            promises,
          };
          resolve(retObj);
        }
      }
      f();
    });
  }

  function getAllAloftData(allAirportsVisible, departureTime, maximumAltitudeAllowed, settings) {
    let promises = [];
    let anyUndefined = false;
    checkAloftCache(allAirportsVisible, true, maximumAltitudeAllowed, settings).then((tmpObj) => {
      anyUndefined = tmpObj.anyUndefined;
      promises = tmpObj.promises;
      if (anyUndefined) {
        // Broadcasting progress event to initiate clouds loading
        Emitter.emit(EventConst.promiseProgress, {
          progressPercent: Math.floor(Math.random() * 6) + 1,
          totalRequest: allAirportsVisible.length,
          requestCompleted: 0,
        });
      }
      allWithProgress(promises, allAirportsVisible, settings, (progress, requestCompleted) => {
        progress = Math.round(progress * 100);
        if (anyUndefined) {
          Emitter.emit(EventConst.promiseProgress, {
            progressPercent: progress,
            totalRequest: allAirportsVisible.length,
            requestCompleted,
          });
        }
      }, departureTime);
    });
  }

  function isAirportNotInRoute(settings, icaoId) {
    return (settings.start !== icaoId
      && settings.end !== icaoId
      && settings.mid1 !== icaoId
      && settings.mid2 !== icaoId);
  }
  content.filterOutOverlappingAirport = (
    sourceData,
    axisObject,
    bnOverlapTextRefWidth,
    settings,
    aloftData,
  ) => {
    let i = 0;
    let filteredRoute = [];
    // console.log(sourceData);
    axisObject.x.domain(d3.extent(sourceData, (d) => d.arrival_time));
    while (i < sourceData.length) {
      sourceData[i].xTempValue = axisObject.x(sourceData[i].arrival_time);
      if (i === 0) {
        // Push first airport without any condition
        filteredRoute.push(sourceData[i]);
      } else if (filteredRoute.length !== 1
        && (i === sourceData.length - 1
          || sourceData[i].icao_id === settings.mid1
          || sourceData[i].icao_id === settings.mid2)) {
        // Last airport will always be inserted
        // For last airport if pre-pushed airport is causing overlap then pop it out
        if ((sourceData[i].xTempValue - filteredRoute[filteredRoute.length - 1].xTempValue)
          < bnOverlapTextRefWidth) {
          filteredRoute.pop();
        }
        filteredRoute.push(sourceData[i]);
      } else if ((sourceData[i].xTempValue - filteredRoute[filteredRoute.length - 1].xTempValue)
        > bnOverlapTextRefWidth) {
        filteredRoute.push(sourceData[i]);
      } else if ((filteredRoute.length > 1) && (sourceData[i].frl < filteredRoute[filteredRoute
        .length - 1].frl) && filteredRoute && filteredRoute.length && isAirportNotInRoute(
        settings,
        filteredRoute[filteredRoute.length - 1].icao_id,
      )) {
        // Picking airport checkpoint that has the worst flight rule
        // Provided any of route airport is not poped
        filteredRoute.pop();
        filteredRoute.push(sourceData[i]);
      }
      i += 1;
    }
    if (settings.testRouteTrend) {
      filteredRoute = sourceData;
    }
    // console.log(settings);
    // if (utilityService.isCordovaApplication()) {
    // Creating map only if cordova application because in web screen size can be variant
    const aloftMapData = useAloftFromMapObject(settings.departureTime);
    // }
    if (!aloftData && !aloftMapData) {
      getAllAloftData(
        filteredRoute,
        settings.departureTime,
        settings.maximumAltitudeAllowed,
        settings,
      );
    }
    // This will prevent from drawing chart if aloft cache is available
    // Saving 300ms in mobile device
    const tmpObj = {
      filteredRoute,
      aloftCacheNotAvailable: !(aloftMapData),
      aloftFromMap: aloftMapData,
    };
    return tmpObj;
  };

  function processRouteData(data, settings) {
    data.travelRoute = getTravelRoute(data, settings, data.flightMode);
    return data;
  }

  content.recalculateRouteData = (data, settings) => {
    const flightMode = bnChartDataService.isMavOrLampFlight(
      settings.departureTime,
      data.totalFlightDuration,
    );
    if (flightMode === ChartConst.products.lamp.name && getDataSourceFactory(settings).fullData) {
      data = getDataSourceFactory(settings).fullData.lamp;
    } else if (flightMode === ChartConst.products.mav.name
      && getDataSourceFactory(settings).fullData) {
      data = getDataSourceFactory(settings).fullData.mav;
    }
    if (!data) {
      // getDataSourceFactory can go null while modal is closed.
      return false;
    }
    if (router.currentRoute.value.name !== 'app.gridview' && getDataSourceFactory(settings)
      .fullData) {
      data.elevationData = getDataSourceFactory(settings).fullData.elevationData;
      data.airsigmet = getDataSourceFactory(settings).fullData.airsigmet;
      data.gairmet = getDataSourceFactory(settings).fullData.gairmet;
    }
    settings.product = flightMode;
    data.flightMode = flightMode;
    return processRouteData(data, settings);
  };

  content.getTravelRoute = (departureTime, settings) => {
    if (getTravelRouteMapObj(settings) && travelRouteMapKey(departureTime)) {
      return getTravelRouteMapObj(settings)[travelRouteMapKey(departureTime)];
    }
    return null;
  };

  content.getLampOrMavObj = (data, settings) => {
    const flightMode = bnChartDataService.isMavOrLampFlight(
      settings.departureTime,
      data.totalFlightDuration,
    );
    if (getDataSourceFactory(settings)) {
      if (flightMode === ChartConst.products.lamp.name) {
        data = utilityService.clone(getDataSourceFactory(settings).fullData.lamp);
      } else if (flightMode === ChartConst.products.mav.name) {
        data = utilityService.clone(getDataSourceFactory(settings).fullData.mav);
      }
    }
    if (data) {
      data.flightMode = flightMode;
    }
    settings.product = flightMode;
    return data;
  };

  content.getWorstRuleForDeparture = (data, settings) => {
    data = content.getLampOrMavObj(data, settings);
    if (data) {
      data.travelRoute = getTravelRoute(data, settings, data.flightMode);
      return data;
    }
    return null;
  };
  content.getRouteProfileData = (
    settings,
    dataMode,
    sidenavCtrl,
    openRouteProfileFormModal,
  ) => new Promise((resolve) => {
    // Clean alll the factory data
    resetTravelRouteMapObj(settings);
    if (getDataSourceFactory(settings)) {
      getDataSourceFactory(settings).cleanFactory();
    }
    const validRoute = utilityService.hasValidRoute({
      icao_id: settings.start,
    }, {
      icao_id: settings.mid1,
    }, {
      icao_id: settings.mid2,
    }, {
      icao_id: settings.end,
    });
    if (validRoute.isValidRoute === false) {
      openRouteProfileFormModal();
      resolve(validRoute);
    }
    bnChartDataService.getAllRequiredData(settings, dataMode, sidenavCtrl).then((fullData) => {
      if (fullData) {
        let data;
        let processedData;
        // Initially go LAMP mode
        const flightMode = bnChartDataService.isMavOrLampFlight(
          settings.departureTime,
          fullData.lamp.totalFlightDuration,
        );
        if (flightMode === ChartConst.products.lamp.name) {
          data = fullData.lamp;
        } else if (flightMode === ChartConst.products.mav.name) {
          data = fullData.mav;
        }
        // data = fullData.lamp;
        if (data) {
          settings.product = ChartConst.products.lamp.name;
          processedData = processRouteData(data, settings);
          if (fullData.mav[0] && fullData.mav[0].forecast) {
            // picking last record for mavEndsAt
            data.mavEndsAt = fullData.mav[0].forecast[fullData.mav[0].forecast.length - 1]
              .forecast_ts;
          }
          processedData.bnforecastAllMets = fullData.bnforecastAllMets;
          processedData.elevationData = fullData.elevationData;
          processedData.airsigmet = fullData.airsigmet;
          processedData.gairmet = fullData.gairmet;
          processedData.waitForAloft = fullData.waitForAloft;

          processedData.lampStartAt = utilityService.roundTimeToNearest30Minutes();
          if ((dataMode === 'leafletmapview' || dataMode === 'mapview') && AppConst
            .metServiceProvider === AppConst.possibleMetServiceProvider.aviationmet) {
            bnChartDataService.getAviationMets(settings, dataMode).then((
              aviationMetData,
            ) => {
              if (aviationMetData && fullData.bnforecastAllMets && fullData
                .bnforecastAllMets.airsigmet) {
                fullData.bnforecastAllMets.airsigmet.forEach((item) => {
                  aviationMetData.push(item);
                });
              }
              processedData.aviationMet = aviationMetData;
              resolve(processedData);
            });
          } else {
            resolve(processedData);
          }
        } else {
          resolve(null);
        }
      } else {
        resolve(null);
      }
    });
  });
  return content;
}
export default routeProfileService();
